// How the fog is drawn on the board (issue 851). The fog is part of the one
// visibility rule, not an overlay: a player's covered squares are already
// black in the masked map the server hands them, and what stands under them
// is absent from their state. Their board still paints those squares black,
// under their tokens: with no map picture there is no masked map, and the
// plain floor and grid showed through (qa-runs 403).
// The DM sees everything, with the covered squares shaded over the map and
// the tokens so they know what the players cannot see; looking through a
// player's eyes, the DM's picture is still the original, so the covered
// squares are painted opaque to show exactly what that player sees.
import type { Grid, Span } from '@dragons-table/vtt-shared';
import { spanRects } from './areaGeometry';

interface Extent { width: number; height: number }
interface FogLike { on: boolean; revealed: Span[] }

export type FogStyle = 'none' | 'shade' | 'opaque';
/** The DM's shade: dark enough to read as "they cannot see this", light enough to see what is there. */
export const FOG_SHADE_ALPHA = 0.55;

/** Who draws what: the DM a shade; a player, and the DM viewing as one, the black the player's map carries. */
export function fogStyle(role: string, viewingAs: boolean): FogStyle {
  if (viewingAs) return 'opaque';
  return role === 'dm' ? 'shade' : 'opaque';
}

/** The covered squares as row spans: each row's complement of the revealed spans, over a board of `cols` × `rows`. */
export function coveredSpans(revealed: readonly Span[], cols: number, rows: number): Span[] {
  const out: Span[] = [];
  if (cols <= 0) return out;
  const byRow = new Map<number, Span[]>();
  for (const s of revealed) { const r = byRow.get(s.j); if (r) r.push(s); else byRow.set(s.j, [s]); }
  for (let j = 0; j < rows; j += 1) {
    let i = 0;
    for (const s of (byRow.get(j) ?? []).sort((a, b) => a.i0 - b.i0)) {
      if (s.i0 > i) out.push({ j, i0: i, i1: Math.min(cols - 1, s.i0 - 1) });
      i = Math.max(i, s.i1 + 1);
      if (i >= cols) break;
    }
    if (i < cols) out.push({ j, i0: i, i1: cols - 1 });
  }
  return out;
}

/** What the fog layer paints: one rectangle per covered span and its alpha, or null for nothing (a player, or the fog off). */
export function fogPaint(fog: FogLike | null | undefined, style: FogStyle, grid: Grid, extent: Extent, board: { cols: number; rows: number }): { rects: Array<{ x: number; y: number; width: number; height: number }>; alpha: number } | null {
  if (style === 'none' || !fog || !fog.on) return null;
  return { rects: spanRects(coveredSpans(fog.revealed, board.cols, board.rows), grid, extent), alpha: style === 'opaque' ? 1 : FOG_SHADE_ALPHA };
}

/** Explored but not in sight now (issue 1144; design/30 §1.7 without light): dimmed, so the player tells the two apart. */
export const SIGHT_DIM_ALPHA = 0.5;

interface SightLike { on: boolean; revealed: Span[]; explored?: Span[]; seen?: Span[]; dim?: Span[]; grey?: Span[] }
/**
 * Light and darkness (issue 1145; design/30 §1.7): in a dim or dark scene (the player's fog then carries `dim`), the
 * explored squares out of sight — unlit, or out of line — are drawn darker, and the seen squares lit only dimly are
 * shaded lightly. Per square, not a polygon per light: the server's per-square sight already cut each light by the
 * walls, and berry draws WebGL in software, so a few rectangles stay cheap where a polygon and blur per light would not.
 */
export const SIGHT_DARK_ALPHA = 0.7;
export const LIGHT_DIM_ALPHA = 0.3;
/**
 * Seen with no light (issue 1146; design/30 §1.5, §1.7): the squares the server sent apart as `grey` — seen by darkvision
 * or blindsight alone — are washed with a mid grey, per square as the dim ones are, so they read without colour.
 */
export const SIGHT_GREY = 0x808080;
export const SIGHT_GREY_ALPHA = 0.65;
/**
 * Remembered on a plain dark floor (issue 1409; Tom's C): explored or revealed, out of sight now. Half-black over the
 * dark theme's empty floor is the colour of the space round the board, so those squares read as black; there they are
 * washed grey instead, plainly neither in sight nor never seen. A map picture keeps the darkening, which reads.
 */
export const SIGHT_MEMORY = 0x78716c;
export const SIGHT_MEMORY_ALPHA = 0.25;
export type SightLayer = { rects: Array<{ x: number; y: number; width: number; height: number }>; alpha: number; color?: number };

function spansFrom(keys: ReadonlySet<number>, cols: number): Span[] {
  const out: Span[] = [];
  for (const q of [...keys].sort((a, b) => a - b)) {
    const i = q % cols; const j = Math.floor(q / cols);
    const last = out[out.length - 1];
    if (last && last.j === j && last.i1 === i - 1) last.i1 = i; else out.push({ j, i0: i, i1: i });
  }
  return out;
}

function keysOf(spans: readonly Span[], cols: number, rows: number): Set<number> {
  const out = new Set<number>();
  for (const s of spans) if (s.j < rows) for (let i = s.i0; i <= Math.min(s.i1, cols - 1); i += 1) out.add(s.j * cols + i);
  return out;
}

/**
 * What a player's board paints with the scene's vision on (issue 1144, slice 2): the squares their sight groups never
 * explored (and the DM never revealed) black, and those explored but not seen now dimmed. It draws only what the
 * player was sent — their explored and seen squares — and decides nothing: what stands out of sight is not in their
 * store. Null with vision off (a player's fog then carries neither list).
 */
export function sightPaint(fog: SightLike | null | undefined, grid: Grid, extent: Extent, board: { cols: number; rows: number }, opts: { memory?: boolean } = {}): SightLayer[] | null {
  if (!fog || !fog.explored || !fog.seen) return null;
  const { cols, rows } = board;
  const shown = keysOf([...fog.explored, ...(fog.on ? fog.revealed : [])], cols, rows);
  const seen = keysOf(fog.seen, cols, rows);
  const dim = new Set([...shown].filter((q) => !seen.has(q)));
  const dark = new Set<number>();
  for (let q = 0; q < cols * rows; q += 1) if (!shown.has(q) && !seen.has(q)) dark.add(q);
  const layers: SightLayer[] = [
    { rects: spanRects(spansFrom(dark, cols), grid, extent), alpha: 1 },
    opts.memory
      ? { rects: spanRects(spansFrom(dim, cols), grid, extent), alpha: SIGHT_MEMORY_ALPHA, color: SIGHT_MEMORY }
      : { rects: spanRects(spansFrom(dim, cols), grid, extent), alpha: fog.dim ? SIGHT_DARK_ALPHA : SIGHT_DIM_ALPHA },
  ];
  if (fog.dim) layers.push({ rects: spanRects(spansFrom(keysOf(fog.dim, cols, rows), cols), grid, extent), alpha: LIGHT_DIM_ALPHA });
  if (fog.grey?.length) layers.push({ rects: spanRects(spansFrom(keysOf(fog.grey, cols, rows), cols), grid, extent), alpha: SIGHT_GREY_ALPHA, color: SIGHT_GREY });
  return layers;
}

/** Which of the fog shade and the things lies on top (see where the world is built): the DM's shade over, a player's under. */
export const shadeOrder = (isDm: boolean): ['things', 'fog'] | ['fog', 'things'] => (isDm ? ['things', 'fog'] : ['fog', 'things']);
